import {RcfaComplianceLocationNode, sortPlantsByComplianceScoreBar} from '@domain/rcfa'
import {FlexPage} from '@hconnect/common/components'
import {PlantViewOptions} from '@hconnect/common/consts'
import {useExpandedState} from '@hconnect/common/hooks'
import {getSystemTimezone} from '@hconnect/common/utils'
import {Stack} from '@mui/material'
import {useMemo} from 'react'
import {useTranslation} from 'react-i18next'

import {
  RcfaComplianceCard,
  RcfaComplianceCardBreadcrumbs,
  RcfaComplianceHeaderActions
} from '../components'
import {FEATURE_NAMESPACE} from '../consts'
import {useRcfaComplianceCardsNavigation, useRcfaCompliancePageState} from '../hooks'
import {mapToRcfaCompliancePlantList} from '../mappers'
import {getAllItemIds} from '../utils'

type Props = {
  titleSlot?: React.ReactNode
  aboveTitleSlot?: React.ReactNode
  data: RcfaComplianceLocationNode[] | undefined
  onPlantItemClick: (plantId: string) => void
  inverted?: boolean
}

export const RcfaComplianceCardsView = ({
  titleSlot,
  aboveTitleSlot,
  data,
  onPlantItemClick,
  inverted = false
}: Props) => {
  const {t} = useTranslation(FEATURE_NAMESPACE)
  const {plantView, setPlantView, timeRange, setTimeRange} = useRcfaCompliancePageState()
  const timezone = getSystemTimezone()

  const {currentLocations, locationHistory, enterLocation, goToLocation, locationSiblings} =
    useRcfaComplianceCardsNavigation(data ?? [], onPlantItemClick)
  const plantListData = useMemo(() => mapToRcfaCompliancePlantList(data ?? []), [data])
  const allItemsIds = useMemo(() => getAllItemIds('subLocations', data ?? []), [data])

  const sortedPlantListData = sortPlantsByComplianceScoreBar(plantListData, 'desc')

  const {allExpanded, toggleExpandCollapseAll} = useExpandedState(allItemsIds)

  return (
    <FlexPage
      title={titleSlot}
      aboveTitle={aboveTitleSlot}
      nameBodySx={{
        width: '100%'
      }}
    >
      <Stack sx={{width: '100%'}} spacing={2}>
        <RcfaComplianceCardBreadcrumbs
          locationHistory={locationHistory}
          goToLocation={goToLocation}
          title={t('pageTitle')}
          locationSiblings={locationSiblings}
          plantView={plantView}
        />
        <RcfaComplianceHeaderActions
          timeRange={timeRange}
          setTimeRange={setTimeRange}
          allExpanded={allExpanded}
          toggleExpandCollapseAll={toggleExpandCollapseAll}
          plantView={plantView}
          setPlantView={setPlantView}
          timezone={timezone}
          isMobile={true}
          inverted={inverted}
        />

        {plantView === PlantViewOptions.PLANT_LIST
          ? sortedPlantListData.map((location) => {
              return (
                <RcfaComplianceCard
                  enterLocation={enterLocation}
                  key={location.id}
                  data={location}
                  isMobile={true}
                />
              )
            })
          : Array.isArray(currentLocations) &&
            currentLocations.map((location) => {
              return (
                <RcfaComplianceCard
                  enterLocation={enterLocation}
                  key={location.id}
                  data={location}
                  isMobile={true}
                />
              )
            })}
      </Stack>
    </FlexPage>
  )
}
